import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { 
  Shield, 
  Users, 
  CheckCircle2, 
  XCircle, 
  ArrowLeft,
  RefreshCw,
  Loader2,
  Eye,
  EyeOff,
  Crown,
  Zap,
  AlertTriangle,
  Bot,
  TrendingUp,
  Calendar,
  UserPlus
} from 'lucide-react';

const AdminLeaderboardFix = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [error, setError] = useState(null);

  // Admin emails allowed
  const ADMIN_EMAILS = ['manuemslaclierxter@gmail.com', 'felixmanuela.manda@gmail.com'];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  const runDiagnostic = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/admin/beta-users-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnosticData(response.data);
    } catch (err) {
      console.error('Diagnostic error:', err);
      setError(err.response?.data?.detail || 'Erreur lors du diagnostic');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run diagnostic on mount
  useEffect(() => {
    if (isAdmin && token) {
      runDiagnostic();
    }
  }, [isAdmin, token]);

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-white mb-2">Accès Refusé</h1>
          <p className="text-gray-400 mb-6">
            Cette page est réservée aux administrateurs.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#E5C585] transition-colors"
          >
            Retour au Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-[#D4AF37]/20 bg-gradient-to-r from-[#0A0A0A] to-[#111]">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-white/5 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#D4AF37]" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <Shield className="w-6 h-6 text-[#D4AF37]" />
                  <h1 className="text-2xl font-serif text-white">Admin — Leaderboard</h1>
                </div>
                <p className="text-gray-400 text-sm mt-1">
                  Statistiques et gestion des utilisatrices
                </p>
              </div>
            </div>
            
            <button
              onClick={runDiagnostic}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#E5C585] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {loading ? 'Analyse...' : 'Actualiser'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && !diagnosticData && (
          <div className="text-center py-16">
            <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-4 animate-spin" />
            <p className="text-gray-400">Analyse en cours...</p>
          </div>
        )}

        {/* Diagnostic Results */}
        {diagnosticData && (
          <div className="space-y-8">
            {/* Statistics Section */}
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="text-xl font-semibold text-white">Statistiques d'évolution</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Real Users - Main metric */}
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-xl p-5 col-span-2 md:col-span-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-gray-400 text-sm">Vraies utilisatrices</span>
                  </div>
                  <p className="text-4xl font-bold text-[#D4AF37]">{diagnosticData.summary?.real_users || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Actives dans le leaderboard</p>
                </div>
                
                {/* New Today */}
                <div className="bg-[#0A0A0A] border border-green-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus className="w-5 h-5 text-green-500" />
                    <span className="text-gray-400 text-sm">Aujourd'hui</span>
                  </div>
                  <p className="text-3xl font-bold text-green-400">+{diagnosticData.statistics?.new_today || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Nouvelles inscrites</p>
                </div>
                
                {/* New This Week */}
                <div className="bg-[#0A0A0A] border border-blue-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-400 text-sm">Cette semaine</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-400">+{diagnosticData.statistics?.new_this_week || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">7 derniers jours</p>
                </div>
                
                {/* New This Month */}
                <div className="bg-[#0A0A0A] border border-purple-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-400 text-sm">Ce mois</span>
                  </div>
                  <p className="text-3xl font-bold text-purple-400">+{diagnosticData.statistics?.new_this_month || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">30 derniers jours</p>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-green-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Eye className="w-5 h-5 text-green-500" />
                  <span className="text-gray-400 text-sm">Visibles</span>
                </div>
                <p className="text-3xl font-bold text-green-400">{diagnosticData.summary?.visible_in_leaderboard || 0}</p>
              </div>
              
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-orange-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Bot className="w-5 h-5 text-orange-500" />
                  <span className="text-gray-400 text-sm">Auto-exclues</span>
                </div>
                <p className="text-3xl font-bold text-orange-400">{diagnosticData.summary?.auto_excluded || 0}</p>
              </div>
              
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-red-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <EyeOff className="w-5 h-5 text-red-500" />
                  <span className="text-gray-400 text-sm">Masquées</span>
                </div>
                <p className="text-3xl font-bold text-red-400">{diagnosticData.summary?.manually_hidden || 0}</p>
              </div>
              
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-400 text-sm">Total en base</span>
                </div>
                <p className="text-3xl font-bold text-gray-400">{diagnosticData.summary?.total_in_database || 0}</p>
              </div>
            </div>

            {/* Auto System Info */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <Bot className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white mb-2">Système automatique actif</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Les comptes contenant ces patterns sont automatiquement exclus :
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {diagnosticData.auto_exclusion_patterns?.map((pattern, i) => (
                      <span key={i} className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                        {pattern}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Visible Users Section */}
            {diagnosticData.visible_users?.length > 0 && (
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-green-500/30 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-green-500/20 bg-green-500/5">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                    <div>
                      <h3 className="font-semibold text-white">
                        Visibles dans le Leaderboard ({diagnosticData.visible_users.length})
                      </h3>
                      <p className="text-sm text-gray-400">
                        Ces utilisatrices apparaissent dans le classement
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="max-h-80 overflow-y-auto">
                  {diagnosticData.visible_users.map((u, index) => (
                    <div
                      key={u.email || index}
                      className="flex items-center justify-between p-4 border-b border-white/5"
                    >
                      <div className="flex items-center gap-4">
                        {u.status?.includes('PROTECTED') ? (
                          <Crown className="w-4 h-4 text-[#D4AF37]" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        )}
                        <div>
                          <p className="font-medium text-white">{u.name || 'Sans nom'}</p>
                          <p className="text-sm text-gray-400">{u.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          u.status?.includes('PROTECTED') 
                            ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {u.status?.includes('PROTECTED') ? 'Admin' : 'Visible'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manually Hidden Users */}
            {diagnosticData.manually_hidden_users?.length > 0 && (
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-red-500/30 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-red-500/20 bg-red-500/5">
                  <div className="flex items-center gap-3">
                    <EyeOff className="w-6 h-6 text-red-500" />
                    <div>
                      <h3 className="font-semibold text-white">
                        Masquées manuellement ({diagnosticData.manually_hidden_users.length})
                      </h3>
                      <p className="text-sm text-gray-400">
                        Ces comptes sont masqués par email ou nom
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="max-h-40 overflow-y-auto">
                  {diagnosticData.manually_hidden_users.map((u, index) => (
                    <div
                      key={u.email || index}
                      className="flex items-center justify-between p-3 border-b border-white/5 text-sm"
                    >
                      <div className="flex items-center gap-3">
                        <EyeOff className="w-4 h-4 text-red-500" />
                        <div>
                          <span className="text-gray-300">{u.name || 'Sans nom'}</span>
                          <span className="text-gray-500 ml-2">({u.email})</span>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">
                        {u.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Auto-Excluded Users Section */}
            {diagnosticData.auto_excluded_users?.length > 0 && (
              <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-orange-500/30 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-orange-500/20 bg-orange-500/5">
                  <div className="flex items-center gap-3">
                    <Bot className="w-6 h-6 text-orange-500" />
                    <div>
                      <h3 className="font-semibold text-white">
                        Auto-exclues ({diagnosticData.auto_excluded_users.length})
                      </h3>
                      <p className="text-sm text-gray-400">
                        Comptes de test détectés automatiquement
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="max-h-40 overflow-y-auto">
                  {diagnosticData.auto_excluded_users.map((u, index) => (
                    <div
                      key={u.email || index}
                      className="flex items-center justify-between p-3 border-b border-white/5 text-sm"
                    >
                      <div className="flex items-center gap-3">
                        <Bot className="w-4 h-4 text-orange-500" />
                        <span className="text-gray-400">{u.email}</span>
                      </div>
                      <span className="px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full">
                        Pattern: "{u.matched_pattern}"
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success Message */}
            <div className="p-5 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-start gap-4">
                <Zap className="w-6 h-6 text-green-500 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white mb-1">Système 100% Automatique</h3>
                  <p className="text-gray-400 text-sm">
                    Aucune intervention manuelle nécessaire. Toutes les vraies utilisatrices apparaissent automatiquement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeaderboardFix;
